/**
 * Constants and default values for PM adapter
 */

import type { TextRun, TrackedChangeKind } from '@superdoc/contracts';
import type { HyperlinkConfig } from './types.js';

export { SUBSCRIPT_SUPERSCRIPT_SCALE } from '@superdoc/contracts';

/**
 * Unit conversion constants
 */
export const TWIPS_PER_INCH = 1440;
export const PX_PER_INCH = 96;
export const PX_PER_PT = 96 / 72;

/**
 * Tracked changes mark types
 */
export const TRACK_INSERT_MARK = 'trackInsert';
export const TRACK_DELETE_MARK = 'trackDelete';
export const TRACK_FORMAT_MARK = 'trackFormat';

/**
 * Map mark types to tracked change kinds
 */
export const TRACK_CHANGE_KIND_MAP: Record<string, TrackedChangeKind> = {
  [TRACK_INSERT_MARK]: 'insert',
  [TRACK_DELETE_MARK]: 'delete',
  [TRACK_FORMAT_MARK]: 'format',
};

/**
 * Tracked change priority for selecting between overlapping marks
 */
export const TRACK_CHANGE_PRIORITY: Record<TrackedChangeKind, number> = {
  insert: 3,
  delete: 3,
  format: 1,
};

/**
 * Valid tracked changes mode values.
 * Used for runtime validation to prevent unsafe type casts.
 */
export const VALID_TRACKED_MODES = ['review', 'original', 'final', 'off'] as const;

/**
 * Maximum allowed length for JSON-stringified run mark payloads.
 * Set to 10KB to balance flexibility with DoS protection.
 */
export const MAX_RUN_MARK_JSON_LENGTH = 10_000;

/**
 * Maximum number of marks allowed in before/after arrays.
 * Prevents memory exhaustion from malicious payloads while supporting
 * reasonable formatting complexity.
 */
export const MAX_RUN_MARK_ARRAY_LENGTH = 100;

/**
 * Maximum nesting depth for mark attribute objects.
 * Protects against stack overflow from deeply nested structures.
 */
export const MAX_RUN_MARK_DEPTH = 5;

/**
 * Default hyperlink configuration
 */
export const DEFAULT_HYPERLINK_CONFIG: HyperlinkConfig = {
  enableRichHyperlinks: false,
};

/**
 * Atomic inline node types that cannot contain content.
 *
 * These nodes have `atom: true` in their ProseMirror schema definition, meaning they:
 * - Occupy exactly 1 position in the document (not 2 like container nodes)
 * - Cannot be directly edited or contain nested content
 * - Are treated as a single unit for selection and cursor positioning
 *
 * CRITICAL: This set must stay in sync with schema definitions. If a node type has
 * `atom: true` in its schema but is NOT listed here, buildPositionMap() will incorrectly
 * calculate positions, leading to cursor positioning bugs during hit testing.
 *
 * Node types with atom: true:
 * - image: Inline images
 * - hardBreak, lineBreak: Line breaks
 * - page-number, total-page-number: Document tokens
 * - indexEntry: Index entry field markers (see index-entry.js)
 * - tab: Tab stops (see tab.js)
 * - noBreakHyphen: Non-breaking hyphen (U+2011 from <w:noBreakHyphen/>; see no-break-hyphen.js)
 * - passthroughInline: Passthrough content like FORMCHECKBOX (see passthrough.js)
 * - bookmarkEnd: Bookmark end markers (see bookmark-end.js)
 *
 * Note: bookmarkStart is NOT atomic - it has content: 'inline*' in the schema,
 * allowing it to wrap inline content and therefore occupying 2 positions (open + close).
 */
export const ATOMIC_INLINE_TYPES = new Set([
  'image',
  'hardBreak',
  'lineBreak',
  'page-number',
  'total-page-number',
  'indexEntry',
  'tab',
  'noBreakHyphen',
  'footnoteReference',
  'mathInline',
  'passthroughInline',
  'bookmarkEnd',
  'fieldAnnotation',
  'documentStatField',
]);

/**
 * Token inline types mapping
 */
export const TOKEN_INLINE_TYPES = new Map<string, TextRun['token']>([
  ['page-number', 'pageNumber'],
  ['total-page-number', 'totalPageCount'],
]);
